import { useCallback, useEffect } from 'react';

import { useDispatch } from 'react-redux';

import { ApiComment } from 'src/components/conversation-provider/types';
import { FETCH_COMMENTS, CLEAR_COMMENTS } from 'src/redux/pull-request/actions';
import urls from 'src/urls/commit';
import authRequest from 'src/utils/fetch';

export const useFetchCommentsActions = () => {
  const fetchComments = useCallback(async (url: string) => {
    try {
      const response = await fetch(authRequest(url));

      if (!response.ok) {
        throw new Error('An error occured while fetching conversations');
      }

      const json = await response.json();

      const commentValues = json.values;

      const nextUrl = json.next || '';

      return {
        commentValues,
        nextUrl,
      };
    } catch (e) {
      return { error: e };
    }
  }, []);

  return { fetchComments };
};

const completeComments = async (
  fetchComments: any,
  dispatch: any,
  containerId: string,
  url: string,
  previousPagesComments: any
) => {
  const {
    nextUrl,
    commentValues,
    error,
  }: {
    nextUrl: string;
    commentValues?: ApiComment[];
    error?: string;
  } = await fetchComments(url);

  if (error) {
    dispatch({
      type: FETCH_COMMENTS.ERROR,
      error: true,
    });
  }

  const isDoneFetching = !nextUrl;
  const allComments = [...previousPagesComments, ...(commentValues || [])];

  if (isDoneFetching) {
    dispatch({
      type: FETCH_COMMENTS.SUCCESS,
      payload: {
        rawComments: allComments,
      },
    });
  } else {
    completeComments(
      fetchComments,
      dispatch,
      containerId,
      nextUrl,
      allComments
    );
  }
};

export const useFetchCommitCommentsActions = () => {
  const fetchCommitComments = useCallback(
    async (
      fetchComments: any,
      dispatch: any,
      containerId: string,
      url: string
    ) => {
      dispatch({
        type: FETCH_COMMENTS.REQUEST,
      });
      await completeComments(fetchComments, dispatch, containerId, url, []);
    },
    []
  );

  return {
    fetchCommitComments,
  };
};

export function useFetchCommitComments(
  user: string,
  repoSlug: string,
  commitId: string
): void {
  const { fetchCommitComments } = useFetchCommitCommentsActions();
  const { fetchComments } = useFetchCommentsActions();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!repoSlug || !commitId) {
      return;
    }
    const containerId =
      `ari:cloud:bitbucket:${user}-${repoSlug}:pullrequest/${commitId}`.toLowerCase();
    fetchCommitComments(
      fetchComments,
      dispatch,
      containerId,
      urls.api.v20.comments(repoSlug, commitId)
    );

    // clear the comments from redux so they don't appear on PR page during
    // load
    // eslint-disable-next-line consistent-return
    return () => {
      dispatch({
        type: CLEAR_COMMENTS,
      });
    };
    // The url is missing because during an update to the dest repo and dest hash
    // the dest repo can update before the dest hash, which results in a call with
    // old dest hash and new dest hash
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCommitComments, user, repoSlug, commitId, fetchComments, dispatch]);
}
